import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer } from '@base-ui/react/drawer';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectUser } from '@/entities/user';
import { ProfitInfo } from '@/entities/profit';
import { isMenuItemActive, useMenuItems, type SidebarMenuItem } from '@/entities/sidebar';
import { LanguageSwitcher } from '@/features/language';
import { useLogout } from '@/features/auth';
import { setMobileSidebar } from '@/features/sidebar';
import { Icon } from '@/shared/IconCollecion';
import { Logo } from '@/shared/ui/Logo';
import { initials } from '@/shared/lib/initials';

// Меню телефона на весь экран для compactNav = 'rail-topbar', макет telecom
// (раздел 5b): крестик и логотип, карточка профиля с балансом, пункты с иконками
// (у группы — раскрывающиеся подпункты), язык и «Выйти» внизу. Открывается
// бургером верхней панели (MobileTopBar); стейт общий со шторкой других брендов
// (sidebar.isMobileSidebarOpen), так что закрытие по навигации работает так же.
//
// Base UI Drawer, а не свой оверлей: фокус, Escape и возврат фокуса на бургер
// уже есть. modal=false — как у шторки: без блокировки скролла body (см.
// NotificationsDrawer.tsx), меню и так закрывает экран целиком.

const rowClassName = (active: boolean) =>
  cn(
    'flex w-full items-center gap-3.5 rounded-surface-sm px-3.5 py-[13px] text-left text-base font-semibold leading-5 transition-colors',
    active
      ? 'bg-action-primary text-content-inverse'
      : 'text-content-inverse-muted hover:bg-surface-sidebar-hover hover:text-content-inverse',
  );

const MenuGroup = ({
  item,
  pathname,
  onNavigate,
}: {
  item: SidebarMenuItem;
  pathname: string;
  onNavigate: () => void;
}) => {
  const active = isMenuItemActive(item, pathname);
  // Раскрыта сразу, если в ней текущий экран, — иначе партнёр не видит, где он.
  const [open, setOpen] = useState(active);

  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(rowClassName(false), active && 'text-content-inverse')}
      >
        <Icon name={item.iconName} color="currentColor" className="size-5 shrink-0" />
        <span className="flex-1">{item.label}</span>
        <Icon
          name="Arrow_down"
          color="currentColor"
          className={cn('size-4 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <ul className="mb-1 ml-6 flex flex-col gap-0.5 border-l-[1.5px] border-line-inverse pl-3.5">
          {item.sublinks.map((sub) => {
            const subActive = pathname.startsWith(sub.link);

            return (
              <li key={sub.link}>
                <Link
                  to={sub.link}
                  onClick={onNavigate}
                  aria-current={subActive ? 'page' : undefined}
                  className={cn(rowClassName(subActive), 'py-[11px] text-[15px]')}
                >
                  {sub.label}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};

export const MobileMenu = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAppSelector(selectUser);
  const isOpen = useAppSelector((state) => state.sidebar.isMobileSidebarOpen);
  const items = useMenuItems();
  const logout = useLogout();
  const { t } = useTranslation(['common', 'settings']);

  const close = () => dispatch(setMobileSidebar(false));

  const handleLogout = () => {
    close();
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={(open) => dispatch(setMobileSidebar(open))}
      swipeDirection="left"
      modal={false}
    >
      <Drawer.Portal>
        <Drawer.Popup
          data-testid="mobile-menu"
          className="fixed inset-0 z-50 flex flex-col bg-surface-sidebar text-content-inverse outline-none transition-transform duration-200 ease-out data-[ending-style]:-translate-x-full data-[starting-style]:-translate-x-full md:hidden"
        >
          <Drawer.Content className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <div className="flex h-[60px] shrink-0 items-center gap-2 pl-2 pr-3">
              <button
                type="button"
                aria-label={t('close')}
                className="flex size-11 items-center justify-center text-content-inverse"
                onClick={close}
              >
                <Icon name="Close" color="currentColor" className="size-[22px]" />
              </button>
              <Logo min={false} size="small" />
            </div>

            <Link
              to="/settings"
              onClick={close}
              className="mx-4 mt-3 flex items-center gap-3 rounded-2xl bg-surface-sidebar-hover p-4"
            >
              <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-content-primary text-sm font-bold">
                {initials(user?.username)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-bold">{user?.username}</span>
                {user?.user_id !== undefined && (
                  <span className="mt-0.5 block text-xs text-content-inverse-muted">
                    {t('settings:partnerId')} {user.user_id}
                  </span>
                )}
              </span>
              <span className="shrink-0 text-right">
                <span className="block text-[11px] text-content-inverse-muted">{t('profit.balance')}</span>
                <ProfitInfo variant="amount" className="block text-[15px] font-extrabold" />
              </span>
            </Link>

            <nav className="px-4 pt-4">
              <ul className="flex flex-col gap-0.5">
                {items.map((item) =>
                  item.sublinks.length > 0 ? (
                    <MenuGroup key={item.link} item={item} pathname={pathname} onNavigate={close} />
                  ) : (
                    <li key={item.link}>
                      <Link
                        to={item.link}
                        onClick={close}
                        aria-current={isMenuItemActive(item, pathname) ? 'page' : undefined}
                        className={rowClassName(isMenuItemActive(item, pathname))}
                      >
                        <Icon name={item.iconName} color="currentColor" className="size-5 shrink-0" />
                        <span className="flex-1">{item.label}</span>
                        {Boolean(item.badge) && (
                          <span
                            className={cn(
                              'flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-[5px] text-[10px] font-bold',
                              isMenuItemActive(item, pathname)
                                ? 'bg-content-inverse text-content-accent'
                                : 'bg-action-primary text-content-inverse',
                            )}
                          >
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    </li>
                  ),
                )}
              </ul>
            </nav>

            <div className="mx-4 mt-auto flex items-center justify-between px-3.5 pt-6 pb-[13px] text-base font-semibold text-content-inverse-muted">
              {t('language.switch')}
              <LanguageSwitcher tone="inverse" placement="top" />
            </div>

            <button
              type="button"
              onClick={handleLogout}
              className="mx-4 mb-6 flex items-center gap-3.5 border-t border-line-inverse px-3.5 py-[13px] text-left text-base font-semibold text-content-inverse-muted hover:text-content-inverse"
            >
              <Icon name="Log_Out" color="currentColor" className="size-5 shrink-0" />
              {t('logout')}
            </button>
          </Drawer.Content>
        </Drawer.Popup>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default MobileMenu;
